import { useEffect, useState, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";

/**
 * Wrap routes that require MFA-verified sessions for vets/admins.
 * - If user is not signed in: redirects to /login.
 * - If route is under /admin/* and user is not an admin: redirects to /my-dashboard.
 * - If route is under /vet-dashboard/* and user is neither vet nor admin: redirects to /my-dashboard.
 * - If session is already aal2 (MFA-verified): passes through.
 * - If user has MFA enrolled but session is aal1: redirects to /mfa.
 * - If user has no MFA enrolled: redirects to /settings to set it up.
 */
const RequireMfa = ({ children }: { children: ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const { role, loading: roleLoading, timedOut: roleTimedOut } = useUserRole();
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (authLoading || roleLoading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }

    // Route-specific role gating: prevent non-admins from rendering the admin
    // UI shell, and non-vets/admins from rendering the vet dashboard shell.
    // RLS already prevents data access, but this avoids leaking the UI surface.
    // If role resolution timed out (e.g. PostgREST schema-cache outage), don't
    // bounce admins/vets to /my-dashboard - keep showing the loader instead so
    // the next role refresh can land them in the right place.
    const path = location.pathname;
    const isAdminRoute = path === "/admin" || path.startsWith("/admin/");
    const isVetRoute = path === "/vet-dashboard" || path.startsWith("/vet-dashboard/");

    if (isAdminRoute && role !== "admin") {
      if (roleTimedOut) return;
      navigate("/my-dashboard", { replace: true });
      return;
    }
    if (isVetRoute && role !== "vet" && role !== "admin") {
      if (roleTimedOut) return;
      // Vets who signed up but are not yet approved see the pending page.
      const signedUpAsVet = user.user_metadata?.is_vet === true;
      navigate(signedUpAsVet ? "/vet-pending" : "/my-dashboard", { replace: true });
      return;
    }

    const isVetOrAdmin = role === "vet" || role === "admin";
    if (!isVetOrAdmin) {
      setAllowed(true);
      setChecking(false);
      return;
    }

    (async () => {
      try {
        // Check if MFA is actually required for this user (admin may have exempted them).
        const { data: required } = await supabase.rpc("mfa_required_for", { _user_id: user.id });
        if (cancelled) return;
        if (required === false) {
          setAllowed(true);
          setChecking(false);
          return;
        }

        const [{ data: aal }, { data: factors }] = await Promise.all([
          supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
          supabase.auth.mfa.listFactors(),
        ]);
        if (cancelled) return;
        const verified = factors?.totp?.find((f) => f.status === "verified");
        if (aal?.currentLevel === "aal2") {
          setAllowed(true);
          setChecking(false);
          return;
        }
        if (verified) {
          navigate("/mfa", {
            replace: true,
            state: { from: location.pathname + location.search },
          });
          return;
        }
        // No MFA set up yet - push them to Settings to enroll.
        navigate("/settings?mfa=required", { replace: true });
      } catch (error) {
        console.error("MFA gate failed", error);
        if (cancelled) return;
        setAllowed(true);
        setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, role, authLoading, roleLoading, roleTimedOut, navigate, location.pathname, location.search]);

  if (checking || !allowed) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  return <>{children}</>;
};

export default RequireMfa;
